import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAdminAuth } from '../context/AdminAuthContext'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { adminFetch } from '../hooks/useAdminFetch'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TenantDetail {
  id: string
  name: string
  plan: string
  created_at: string | null
  trial_expires_at: string | null
  rate_limit_override: { requests_per_min?: number; ai_calls_per_day?: number; expires_at?: string } | null
  settings: Record<string, unknown>
  kpi_30d: {
    uploads_total: number
    rows_imported: number
    errors_total: number
    jobs_failed: number
  }
  trend_6m: Array<{ month: string; uploads: number; ai_calls: number }>
  sources: Array<{ provider: string; last_polled_at: string | null; is_active: boolean }>
  users: Array<{
    id: string
    email: string | null
    role: string
    last_sign_in_at: string | null
    created_at: string | null
  }>
}

interface PlanHistoryItem {
  event_type: string
  payload: Record<string, unknown>
  created_at: string | null
  admin_email: string | null
}

interface JobItem {
  id: string
  file_name: string
  status: string
  rows_valid: number | null
  rows_error: number | null
  created_at: string | null
  completed_at: string | null
  has_r2: boolean
}

interface JobsResponse {
  items: JobItem[]
  total: number
  limit: number
  offset: number
}

interface JobErrors {
  job_id: string
  file_name: string
  status: string
  rows_error: number
  errors: Array<{ row: number; field: string; error_it: string }>
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PLAN_COLORS: Record<string, string> = {
  trial: 'bg-yellow-500/15 text-yellow-300',
  starter: 'bg-blue-500/15 text-blue-300',
  growth: 'bg-purple-500/15 text-purple-300',
  enterprise: 'bg-green-500/15 text-green-300',
}

const STATUS_COLORS: Record<string, string> = {
  completed: 'bg-green-500/15 text-green-300',
  failed: 'bg-red-500/15 text-red-300',
  mapping: 'bg-yellow-500/15 text-yellow-300',
  pending: 'bg-gray-500/15 text-gray-400',
  parsing: 'bg-blue-500/15 text-blue-300',
  validating: 'bg-blue-500/15 text-blue-300',
  storing: 'bg-blue-500/15 text-blue-300',
  kpi_computing: 'bg-blue-500/15 text-blue-300',
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDateShort(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function Badge({ value, colorMap, label }: { value: string; colorMap: Record<string, string>; label?: string }) {
  const cls = colorMap[value] ?? 'bg-gray-500/15 text-gray-400'
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {label ?? value}
    </span>
  )
}

const ROLE_COLORS: Record<string, string> = {
  owner: 'bg-purple-500/15 text-purple-300',
  admin: 'bg-blue-500/15 text-blue-300',
  analyst: 'bg-cyan-500/15 text-cyan-300',
  viewer: 'bg-gray-500/15 text-gray-400',
}

const JOBS_LIMIT = 50

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function KpiCard({ label, value, sub }: { label: string; value: number | string; sub?: string }) {
  return (
    <div
      className="rounded-xl p-4 border"
      style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)' }}
    >
      <div className="text-xs mb-1" style={{ color: 'var(--km-text-muted)' }}>{label}</div>
      <div className="text-2xl font-bold" style={{ color: 'var(--km-text-primary)' }}>{value}</div>
      {sub && <div className="text-xs mt-0.5" style={{ color: 'var(--km-text-muted)' }}>{sub}</div>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Error modal
// ---------------------------------------------------------------------------

function ErrorModal({ errors, onClose }: { errors: JobErrors; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)' }}
      onClick={onClose}
    >
      <div
        className="rounded-xl border w-full max-w-2xl max-h-[80vh] flex flex-col"
        style={{ background: 'var(--km-bg)', borderColor: 'var(--km-border)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: 'var(--km-border)' }}
        >
          <div>
            <div className="font-semibold text-sm" style={{ color: 'var(--km-text-primary)' }}>
              Errori — {errors.file_name}
            </div>
            <div className="text-xs mt-0.5" style={{ color: 'var(--km-text-muted)' }}>
              {errors.rows_error} righe con errori
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-lg leading-none px-2"
            style={{ color: 'var(--km-text-muted)' }}
          >
            ×
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-4 space-y-2">
          {errors.errors.length === 0 ? (
            <p className="text-sm text-center py-4" style={{ color: 'var(--km-text-muted)' }}>
              Nessun dettaglio errore disponibile.
            </p>
          ) : (
            errors.errors.map((e, i) => (
              <div
                key={i}
                className="rounded-lg p-3 border text-sm"
                style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)' }}
              >
                <span className="font-mono text-xs mr-2" style={{ color: 'var(--km-text-muted)' }}>
                  Riga {e.row}
                </span>
                <span className="font-medium mr-2" style={{ color: 'var(--km-text-secondary)' }}>
                  {e.field}:
                </span>
                <span style={{ color: 'var(--km-text-primary)' }}>{e.error_it}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab: Panoramica
// ---------------------------------------------------------------------------

function TabPanoramica({ tenant }: { tenant: TenantDetail }) {
  return (
    <div className="space-y-6">
      {/* Anagrafica */}
      <div
        className="rounded-xl border p-5"
        style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)' }}
      >
        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--km-text-muted)' }}>
          ANAGRAFICA
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-xs mb-1" style={{ color: 'var(--km-text-muted)' }}>ID</div>
            <div className="font-mono text-xs" style={{ color: 'var(--km-text-secondary)' }}>
              {tenant.id}
            </div>
          </div>
          <div>
            <div className="text-xs mb-1" style={{ color: 'var(--km-text-muted)' }}>Piano</div>
            <Badge value={tenant.plan} colorMap={PLAN_COLORS} />
          </div>
          <div>
            <div className="text-xs mb-1" style={{ color: 'var(--km-text-muted)' }}>Creato il</div>
            <div style={{ color: 'var(--km-text-secondary)' }}>{formatDateShort(tenant.created_at)}</div>
          </div>
          <div>
            <div className="text-xs mb-1" style={{ color: 'var(--km-text-muted)' }}>Valuta</div>
            <div style={{ color: 'var(--km-text-secondary)' }}>
              {(tenant.settings as { currency?: string }).currency ?? '—'}
            </div>
          </div>
        </div>
      </div>

      {/* KPI 30gg */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Upload (30gg)" value={tenant.kpi_30d.uploads_total} />
        <KpiCard label="Righe importate" value={tenant.kpi_30d.rows_imported.toLocaleString('it-IT')} />
        <KpiCard label="Errori totali" value={tenant.kpi_30d.errors_total.toLocaleString('it-IT')} />
        <KpiCard label="Job falliti" value={tenant.kpi_30d.jobs_failed} />
      </div>

      {/* Grafico 6 mesi */}
      {tenant.trend_6m.length > 0 && (
        <div
          className="rounded-xl border p-5"
          style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)' }}
        >
          <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--km-text-muted)' }}>
            UTILIZZO ULTIMI 6 MESI
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={tenant.trend_6m} barGap={4}>
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: 'var(--km-text-muted)' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: 'var(--km-text-muted)' }}
                axisLine={false}
                tickLine={false}
                width={28}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--km-surface)',
                  border: '1px solid var(--km-border)',
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelStyle={{ color: 'var(--km-text-muted)' }}
              />
              <Legend wrapperStyle={{ fontSize: 11, color: 'var(--km-text-muted)' }} />
              <Bar dataKey="uploads" name="Upload" fill="#6366f1" radius={[3, 3, 0, 0]} />
              <Bar dataKey="ai_calls" name="AI calls" fill="#06b6d4" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Sorgenti connesse */}
      <div
        className="rounded-xl border p-5"
        style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)' }}
      >
        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--km-text-muted)' }}>
          SORGENTI CONNESSE
        </h3>
        {tenant.sources.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--km-text-muted)' }}>Nessuna sorgente configurata.</p>
        ) : (
          <div className="space-y-2">
            {tenant.sources.map((s, i) => (
              <div
                key={i}
                className="flex items-center justify-between text-sm py-2 border-b last:border-0"
                style={{ borderColor: 'var(--km-border)' }}
              >
                <span className="font-medium capitalize" style={{ color: 'var(--km-text-primary)' }}>
                  {s.provider}
                </span>
                <div className="flex items-center gap-3">
                  <span style={{ color: 'var(--km-text-muted)' }}>
                    Ultimo sync: {formatDate(s.last_polled_at)}
                  </span>
                  <span
                    className={`inline-block w-2 h-2 rounded-full ${s.is_active ? 'bg-green-400' : 'bg-gray-500'}`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Utenti */}
      <div
        className="rounded-xl border overflow-hidden"
        style={{ borderColor: 'var(--km-border)' }}
      >
        <div
          className="px-5 py-3 border-b text-sm font-semibold"
          style={{
            background: 'var(--km-surface)',
            borderColor: 'var(--km-border)',
            color: 'var(--km-text-muted)',
          }}
        >
          UTENTI DEL TENANT
        </div>
        {tenant.users.length === 0 ? (
          <div className="p-5 text-sm" style={{ color: 'var(--km-text-muted)' }}>
            Nessun utente trovato.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr
                className="text-xs uppercase tracking-wider border-b"
                style={{
                  background: 'var(--km-surface)',
                  borderColor: 'var(--km-border)',
                  color: 'var(--km-text-muted)',
                }}
              >
                <th className="text-left px-4 py-2 font-medium">Email</th>
                <th className="text-left px-4 py-2 font-medium">Ruolo</th>
                <th className="text-left px-4 py-2 font-medium">Ultimo accesso</th>
                <th className="text-left px-4 py-2 font-medium">Aggiunto il</th>
              </tr>
            </thead>
            <tbody>
              {tenant.users.map((u) => (
                <tr
                  key={u.id}
                  className="border-b last:border-0"
                  style={{ borderColor: 'var(--km-border)' }}
                >
                  <td className="px-4 py-2.5" style={{ color: 'var(--km-text-primary)' }}>
                    {u.email ?? <span style={{ color: 'var(--km-text-muted)' }}>{u.id.slice(0, 8)}…</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge value={u.role} colorMap={ROLE_COLORS} />
                  </td>
                  <td className="px-4 py-2.5" style={{ color: 'var(--km-text-secondary)' }}>
                    {formatDate(u.last_sign_in_at)}
                  </td>
                  <td className="px-4 py-2.5" style={{ color: 'var(--km-text-secondary)' }}>
                    {formatDateShort(u.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab: Upload & Ingestion
// ---------------------------------------------------------------------------

function TabJobs({ tenantId }: { tenantId: string }) {
  const [statusFilter, setStatusFilter] = useState('')
  const [sinceDays, setSinceDays] = useState('')
  const [offset, setOffset] = useState(0)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [errorModal, setErrorModal] = useState<JobErrors | null>(null)
  const queryClient = useQueryClient()

  const params = new URLSearchParams({ limit: String(JOBS_LIMIT), offset: String(offset) })
  if (statusFilter) params.set('status', statusFilter)
  if (sinceDays) params.set('since_days', sinceDays)

  const { data, isLoading, error } = useQuery<JobsResponse>({
    queryKey: ['admin', 'tenant-jobs', tenantId, statusFilter, sinceDays, offset],
    queryFn: () => adminFetch<JobsResponse>(`/api/v1/admin/tenants/${tenantId}/jobs?${params.toString()}`),
  })

  async function handleReprocess(job: JobItem) {
    if (!window.confirm(`Riprocessare il file "${job.file_name}"?\nIl job verrà rimesso in coda.`)) return
    setActionLoading(`reprocess-${job.id}`)
    try {
      await adminFetch(`/api/v1/admin/tenants/${tenantId}/jobs/${job.id}/reprocess`, { method: 'POST' })
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenant-jobs', tenantId] })
    } catch (e: unknown) {
      alert(`Errore: ${e instanceof Error ? e.message : 'Riprocessamento fallito'}`)
    } finally {
      setActionLoading(null)
    }
  }

  async function handleViewErrors(job: JobItem) {
    setActionLoading(`errors-${job.id}`)
    try {
      const result = await adminFetch<JobErrors>(
        `/api/v1/admin/tenants/${tenantId}/jobs/${job.id}/errors`
      )
      setErrorModal(result)
    } catch (e: unknown) {
      alert(`Errore: ${e instanceof Error ? e.message : 'Impossibile caricare errori'}`)
    } finally {
      setActionLoading(null)
    }
  }

  async function handleFlushCache() {
    if (!window.confirm('Svuotare la cache KPI di questo tenant?\nI prossimi accessi alla dashboard ricalcoleranno i KPI.')) return
    setActionLoading('flush')
    try {
      await adminFetch(`/api/v1/admin/tenants/${tenantId}/flush-cache`, { method: 'POST' })
      alert('Cache KPI svuotata.')
    } catch (e: unknown) {
      alert(`Errore: ${e instanceof Error ? e.message : 'Flush fallito'}`)
    } finally {
      setActionLoading(null)
    }
  }

  const selectStyle: React.CSSProperties = {
    background: 'var(--km-bg)',
    borderColor: 'var(--km-border)',
    color: 'var(--km-text-secondary)',
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap gap-3">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setOffset(0) }}
            className="px-3 py-2 rounded-lg text-sm border outline-none"
            style={selectStyle}
          >
            <option value="">Tutti gli stati</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="mapping">Mapping</option>
            <option value="pending">Pending</option>
          </select>
          <select
            value={sinceDays}
            onChange={(e) => { setSinceDays(e.target.value); setOffset(0) }}
            className="px-3 py-2 rounded-lg text-sm border outline-none"
            style={selectStyle}
          >
            <option value="">Tutto il periodo</option>
            <option value="7">Ultimi 7 giorni</option>
            <option value="30">Ultimi 30 giorni</option>
            <option value="90">Ultimi 90 giorni</option>
          </select>
        </div>
        <button
          onClick={handleFlushCache}
          disabled={actionLoading === 'flush'}
          className="px-3 py-2 rounded-lg text-xs border transition-colors disabled:opacity-40"
          style={{
            background: 'var(--km-surface)',
            borderColor: 'var(--km-border)',
            color: 'var(--km-text-secondary)',
          }}
        >
          {actionLoading === 'flush' ? 'Svuotamento…' : '⚡ Flush cache KPI'}
        </button>
      </div>

      {/* Tabella */}
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--km-border)' }}>
        {isLoading && (
          <div className="p-6 text-sm text-center" style={{ color: 'var(--km-text-muted)' }}>
            Caricamento…
          </div>
        )}
        {error && (
          <div className="p-6 text-sm text-center" style={{ color: 'var(--km-error)' }}>
            Errore nel caricamento dei job.
          </div>
        )}
        {data && !isLoading && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr
                  className="text-xs uppercase tracking-wider border-b"
                  style={{
                    background: 'var(--km-surface)',
                    borderColor: 'var(--km-border)',
                    color: 'var(--km-text-muted)',
                  }}
                >
                  <th className="text-left px-4 py-3 font-medium">File</th>
                  <th className="text-left px-4 py-3 font-medium">Stato</th>
                  <th className="text-right px-4 py-3 font-medium">Righe valide</th>
                  <th className="text-right px-4 py-3 font-medium">Errori</th>
                  <th className="text-left px-4 py-3 font-medium">Caricato il</th>
                  <th className="text-left px-4 py-3 font-medium">Completato</th>
                  <th className="text-right px-4 py-3 font-medium">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-sm" style={{ color: 'var(--km-text-muted)' }}>
                      Nessun job trovato
                    </td>
                  </tr>
                ) : (
                  data.items.map((job) => (
                    <tr
                      key={job.id}
                      className="border-b hover:bg-white/[0.02] transition-colors"
                      style={{ borderColor: 'var(--km-border)' }}
                    >
                      <td className="px-4 py-3">
                        <div
                          className="font-medium max-w-[200px] truncate"
                          style={{ color: 'var(--km-text-primary)' }}
                          title={job.file_name}
                        >
                          {job.file_name ?? '—'}
                        </div>
                        <div className="text-xs font-mono mt-0.5" style={{ color: 'var(--km-text-muted)' }}>
                          {job.id.slice(0, 8)}…
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge value={job.status} colorMap={STATUS_COLORS} />
                      </td>
                      <td className="px-4 py-3 text-right" style={{ color: 'var(--km-text-secondary)' }}>
                        {job.rows_valid ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {job.rows_error ? (
                          <span className="text-red-400 font-medium">{job.rows_error}</span>
                        ) : (
                          <span style={{ color: 'var(--km-text-muted)' }}>—</span>
                        )}
                      </td>
                      <td className="px-4 py-3" style={{ color: 'var(--km-text-secondary)' }}>
                        {formatDate(job.created_at)}
                      </td>
                      <td className="px-4 py-3" style={{ color: 'var(--km-text-secondary)' }}>
                        {formatDate(job.completed_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 justify-end">
                          {(job.status === 'failed' || job.status === 'mapping' || job.status === 'completed') && job.has_r2 && (
                            <button
                              onClick={() => handleReprocess(job)}
                              disabled={actionLoading === `reprocess-${job.id}`}
                              className="px-2 py-1 rounded text-xs border transition-colors disabled:opacity-40"
                              style={{
                                background: 'var(--km-surface)',
                                borderColor: 'var(--km-border)',
                                color: 'var(--km-text-secondary)',
                              }}
                            >
                              {actionLoading === `reprocess-${job.id}` ? '…' : 'Riprocessa'}
                            </button>
                          )}
                          {(job.status === 'failed' || (job.rows_error ?? 0) > 0) && (
                            <button
                              onClick={() => handleViewErrors(job)}
                              disabled={actionLoading === `errors-${job.id}`}
                              className="px-2 py-1 rounded text-xs border transition-colors disabled:opacity-40"
                              style={{
                                background: 'var(--km-surface)',
                                borderColor: 'var(--km-border)',
                                color: 'var(--km-text-secondary)',
                              }}
                            >
                              {actionLoading === `errors-${job.id}` ? '…' : 'Vedi errori'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Paginazione */}
      {data && data.total > JOBS_LIMIT && (
        <div className="flex items-center gap-3 justify-end">
          <button
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - JOBS_LIMIT))}
            className="px-3 py-1.5 rounded-lg text-xs border transition-colors disabled:opacity-40"
            style={{
              background: 'var(--km-surface)',
              borderColor: 'var(--km-border)',
              color: 'var(--km-text-secondary)',
            }}
          >
            Precedente
          </button>
          <span className="text-xs" style={{ color: 'var(--km-text-muted)' }}>
            {offset + 1}–{Math.min(offset + JOBS_LIMIT, data.total)} di {data.total}
          </span>
          <button
            disabled={offset + JOBS_LIMIT >= data.total}
            onClick={() => setOffset(offset + JOBS_LIMIT)}
            className="px-3 py-1.5 rounded-lg text-xs border transition-colors disabled:opacity-40"
            style={{
              background: 'var(--km-surface)',
              borderColor: 'var(--km-border)',
              color: 'var(--km-text-secondary)',
            }}
          >
            Successiva
          </button>
        </div>
      )}

      {errorModal && (
        <ErrorModal errors={errorModal} onClose={() => setErrorModal(null)} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab: Configurazione (Admin v4)
// ---------------------------------------------------------------------------

const PLAN_LABELS: Record<string, string> = {
  trial: 'Trial',
  starter: 'Starter',
  growth: 'Growth',
  enterprise: 'Enterprise',
}

const EVENT_LABELS: Record<string, string> = {
  plan_change: '📋 Cambio piano',
  trial_extended: '⏳ Trial esteso',
  rate_limit_override_set: '⚡ Override rate limit',
}

function TabConfigurazione({ tenant, tenantId }: { tenant: TenantDetail; tenantId: string }) {
  const queryClient = useQueryClient()

  // -- Plan change state --
  const [newPlan, setNewPlan] = useState(tenant.plan)
  const [trialExpiresAt, setTrialExpiresAt] = useState(
    tenant.trial_expires_at ? tenant.trial_expires_at.slice(0, 10) : ''
  )
  const [planNotes, setPlanNotes] = useState('')
  const [planSaving, setPlanSaving] = useState(false)

  // -- Extend trial state --
  const [extendDays, setExtendDays] = useState('')
  const [extendNotes, setExtendNotes] = useState('')
  const [extendSaving, setExtendSaving] = useState(false)

  // -- Rate limit override state --
  const [rlReqPerMin, setRlReqPerMin] = useState(
    tenant.rate_limit_override?.requests_per_min?.toString() ?? ''
  )
  const [rlAiPerDay, setRlAiPerDay] = useState(
    tenant.rate_limit_override?.ai_calls_per_day?.toString() ?? ''
  )
  const [rlExpiresAt, setRlExpiresAt] = useState(
    tenant.rate_limit_override?.expires_at?.slice(0, 10) ?? ''
  )
  const [rlSaving, setRlSaving] = useState(false)

  // -- Plan history --
  const { data: history } = useQuery<{ items: PlanHistoryItem[] }>({
    queryKey: ['admin', 'plan-history', tenantId],
    queryFn: () => adminFetch(`/api/v1/admin/tenants/${tenantId}/plan-history`),
  })

  const inputStyle: React.CSSProperties = {
    background: 'var(--km-bg)',
    borderColor: 'var(--km-border)',
    color: 'var(--km-text-primary)',
  }

  async function handlePlanSave() {
    if (!window.confirm(`Cambiare il piano da "${PLAN_LABELS[tenant.plan]}" a "${PLAN_LABELS[newPlan]}"?`)) return
    setPlanSaving(true)
    try {
      await adminFetch(`/api/v1/admin/tenants/${tenantId}/plan`, {
        method: 'PATCH',
        body: JSON.stringify({
          plan: newPlan,
          trial_expires_at: trialExpiresAt || null,
          notes: planNotes,
        }),
      })
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenant-detail', tenantId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'plan-history', tenantId] })
      setPlanNotes('')
      alert('Piano aggiornato.')
    } catch (e: unknown) {
      alert(`Errore: ${e instanceof Error ? e.message : 'Salvataggio fallito'}`)
    } finally {
      setPlanSaving(false)
    }
  }

  async function handleExtendTrial() {
    const days = parseInt(extendDays, 10)
    if (!days || days < 1) { alert('Inserisci un numero di giorni valido.'); return }
    if (!window.confirm(`Estendere il trial di ${days} giorni?`)) return
    setExtendSaving(true)
    try {
      const result = await adminFetch<{ new_trial_expires_at: string; message: string }>(
        `/api/v1/admin/tenants/${tenantId}/extend-trial`,
        { method: 'POST', body: JSON.stringify({ days, notes: extendNotes }) }
      )
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenant-detail', tenantId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'plan-history', tenantId] })
      setExtendDays('')
      setExtendNotes('')
      alert(result.message)
    } catch (e: unknown) {
      alert(`Errore: ${e instanceof Error ? e.message : 'Estensione fallita'}`)
    } finally {
      setExtendSaving(false)
    }
  }

  async function handleRlSave(removing = false) {
    if (!removing && !rlExpiresAt) { alert('Specifica una data di scadenza override.'); return }
    const action = removing ? 'Rimuovere l\'override rate limit?' : 'Impostare l\'override rate limit?'
    if (!window.confirm(action)) return
    setRlSaving(true)
    try {
      await adminFetch(`/api/v1/admin/tenants/${tenantId}/rate-limit-override`, {
        method: 'PATCH',
        body: JSON.stringify(removing ? {} : {
          requests_per_min: rlReqPerMin ? parseInt(rlReqPerMin, 10) : null,
          ai_calls_per_day: rlAiPerDay ? parseInt(rlAiPerDay, 10) : null,
          expires_at: rlExpiresAt || null,
        }),
      })
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenant-detail', tenantId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'plan-history', tenantId] })
      if (removing) { setRlReqPerMin(''); setRlAiPerDay(''); setRlExpiresAt('') }
      alert(removing ? 'Override rimosso.' : 'Override impostato.')
    } catch (e: unknown) {
      alert(`Errore: ${e instanceof Error ? e.message : 'Salvataggio fallito'}`)
    } finally {
      setRlSaving(false)
    }
  }

  const sectionStyle: React.CSSProperties = {
    background: 'var(--km-surface)',
    borderColor: 'var(--km-border)',
  }

  return (
    <div className="space-y-6">
      {/* --- Cambio piano --- */}
      <div className="rounded-xl border p-5 space-y-4" style={sectionStyle}>
        <h3 className="text-sm font-semibold" style={{ color: 'var(--km-text-muted)' }}>CAMBIO PIANO</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs block mb-1" style={{ color: 'var(--km-text-muted)' }}>Nuovo piano</label>
            <select
              value={newPlan}
              onChange={(e) => setNewPlan(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm border outline-none"
              style={inputStyle}
            >
              {Object.entries(PLAN_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
          {newPlan === 'trial' && (
            <div>
              <label className="text-xs block mb-1" style={{ color: 'var(--km-text-muted)' }}>Scadenza trial</label>
              <input
                type="date"
                value={trialExpiresAt}
                onChange={(e) => setTrialExpiresAt(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm border outline-none"
                style={inputStyle}
              />
            </div>
          )}
        </div>
        <div>
          <label className="text-xs block mb-1" style={{ color: 'var(--km-text-muted)' }}>Note interne (visibili solo agli admin)</label>
          <textarea
            value={planNotes}
            onChange={(e) => setPlanNotes(e.target.value)}
            rows={2}
            placeholder="Es. Cliente ha firmato contratto annuale"
            className="w-full px-3 py-2 rounded-lg text-sm border outline-none resize-none"
            style={inputStyle}
          />
        </div>
        <button
          onClick={handlePlanSave}
          disabled={planSaving || newPlan === tenant.plan}
          className="px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40"
          style={{ background: '#6366f1', color: '#fff', opacity: newPlan === tenant.plan ? 0.4 : 1 }}
        >
          {planSaving ? 'Salvataggio…' : 'Salva modifiche piano'}
        </button>
      </div>

      {/* --- Estensione trial --- */}
      {tenant.plan === 'trial' && (
        <div className="rounded-xl border p-5 space-y-4" style={sectionStyle}>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--km-text-muted)' }}>ESTENSIONE TRIAL</h3>
          <div className="text-sm" style={{ color: 'var(--km-text-secondary)' }}>
            Scadenza attuale:{' '}
            <strong style={{ color: 'var(--km-text-primary)' }}>
              {tenant.trial_expires_at ? formatDateShort(tenant.trial_expires_at) : 'Non impostata'}
            </strong>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs block mb-1" style={{ color: 'var(--km-text-muted)' }}>Estendi di (giorni)</label>
              <input
                type="number"
                min={1}
                max={365}
                value={extendDays}
                onChange={(e) => setExtendDays(e.target.value)}
                placeholder="Es. 14"
                className="w-full px-3 py-2 rounded-lg text-sm border outline-none"
                style={inputStyle}
              />
              {extendDays && !isNaN(parseInt(extendDays, 10)) && (
                <div className="text-xs mt-1" style={{ color: 'var(--km-text-muted)' }}>
                  Nuova scadenza: {new Date(Date.now() + parseInt(extendDays, 10) * 86400000).toLocaleDateString('it-IT')}
                </div>
              )}
            </div>
            <div>
              <label className="text-xs block mb-1" style={{ color: 'var(--km-text-muted)' }}>Note</label>
              <input
                type="text"
                value={extendNotes}
                onChange={(e) => setExtendNotes(e.target.value)}
                placeholder="Motivazione"
                className="w-full px-3 py-2 rounded-lg text-sm border outline-none"
                style={inputStyle}
              />
            </div>
          </div>
          <button
            onClick={handleExtendTrial}
            disabled={extendSaving || !extendDays}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40"
            style={{ background: '#0891b2', color: '#fff' }}
          >
            {extendSaving ? 'Estensione…' : 'Estendi trial'}
          </button>
        </div>
      )}

      {/* --- Override rate limit --- */}
      <div className="rounded-xl border p-5 space-y-4" style={sectionStyle}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--km-text-muted)' }}>OVERRIDE RATE LIMIT TEMPORANEO</h3>
          {tenant.rate_limit_override && (
            <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(251,146,60,0.15)', color: '#fb923c' }}>
              Override attivo
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs block mb-1" style={{ color: 'var(--km-text-muted)' }}>Req/minuto (lascia vuoto = default piano)</label>
            <input
              type="number"
              value={rlReqPerMin}
              onChange={(e) => setRlReqPerMin(e.target.value)}
              placeholder="Es. 500"
              className="w-full px-3 py-2 rounded-lg text-sm border outline-none"
              style={inputStyle}
            />
          </div>
          <div>
            <label className="text-xs block mb-1" style={{ color: 'var(--km-text-muted)' }}>AI calls/giorno</label>
            <input
              type="number"
              value={rlAiPerDay}
              onChange={(e) => setRlAiPerDay(e.target.value)}
              placeholder="Es. 1000"
              className="w-full px-3 py-2 rounded-lg text-sm border outline-none"
              style={inputStyle}
            />
          </div>
          <div>
            <label className="text-xs block mb-1" style={{ color: 'var(--km-text-muted)' }}>Scadenza override</label>
            <input
              type="date"
              value={rlExpiresAt}
              onChange={(e) => setRlExpiresAt(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm border outline-none"
              style={inputStyle}
            />
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => handleRlSave(false)}
            disabled={rlSaving}
            className="px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-40"
            style={{ background: '#d97706', color: '#fff' }}
          >
            {rlSaving ? '…' : 'Imposta override'}
          </button>
          {tenant.rate_limit_override && (
            <button
              onClick={() => handleRlSave(true)}
              disabled={rlSaving}
              className="px-4 py-2 rounded-lg text-sm font-medium border disabled:opacity-40"
              style={{
                background: 'var(--km-bg)',
                borderColor: 'var(--km-border)',
                color: 'var(--km-text-secondary)',
              }}
            >
              Rimuovi override
            </button>
          )}
        </div>
      </div>

      {/* --- Storico piani --- */}
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--km-border)' }}>
        <div
          className="px-5 py-3 text-sm font-semibold border-b"
          style={{ background: 'var(--km-surface)', borderColor: 'var(--km-border)', color: 'var(--km-text-muted)' }}
        >
          STORICO PIANI
        </div>
        {!history || history.items.length === 0 ? (
          <div className="p-5 text-sm" style={{ color: 'var(--km-text-muted)' }}>
            Nessuna modifica registrata.
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: 'var(--km-border)' }}>
            {history.items.map((item, i) => (
              <div key={i} className="px-5 py-3 text-sm space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium" style={{ color: 'var(--km-text-primary)' }}>
                    {EVENT_LABELS[item.event_type] ?? item.event_type}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--km-text-muted)' }}>
                    {formatDate(item.created_at)}
                  </span>
                </div>
                <div style={{ color: 'var(--km-text-secondary)' }}>
                  {item.event_type === 'plan_change' && (
                    <span>{item.payload.old_plan as string} → {item.payload.new_plan as string}</span>
                  )}
                  {item.event_type === 'trial_extended' && (
                    <span>+{item.payload.days as number} giorni (nuova scadenza: {formatDateShort(item.payload.new_expires_at as string)})</span>
                  )}
                  {item.event_type === 'rate_limit_override_set' && (
                    <span>{item.payload.removed ? 'Override rimosso' : `${item.payload.requests_per_min ?? '—'} req/min, ${item.payload.ai_calls_per_day ?? '—'} AI/giorno`}</span>
                  )}
                </div>
                {(item.payload.notes as string) && (
                  <div className="text-xs italic" style={{ color: 'var(--km-text-muted)' }}>
                    "{item.payload.notes as string}"
                  </div>
                )}
                <div className="text-xs" style={{ color: 'var(--km-text-muted)' }}>
                  Operatore: {item.admin_email ?? '—'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Tab: Lifecycle (reset dati + eliminazione tenant)
// ---------------------------------------------------------------------------

function TabLifecycle({ tenant, tenantId }: { tenant: TenantDetail; tenantId: string }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Reset data state
  const [resetConfirm, setResetConfirm] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  const [resetResult, setResetResult] = useState<string | null>(null)
  const [resetError, setResetError] = useState<string | null>(null)

  // Delete tenant state
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [deleteUsers, setDeleteUsers] = useState(true)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const inputStyle: React.CSSProperties = {
    background: 'var(--km-bg)',
    borderColor: 'var(--km-border)',
    color: 'var(--km-text-primary)',
  }

  const sectionStyle: React.CSSProperties = {
    borderColor: 'var(--km-border)',
  }

  async function handleReset() {
    const expected = `RESET ${tenant.name}`
    if (resetConfirm !== expected) {
      setResetError(`Scrivi esattamente: ${expected}`)
      return
    }
    setResetLoading(true)
    setResetError(null)
    setResetResult(null)
    try {
      const result = await adminFetch<{ ok: boolean; deleted_rows: Record<string, number>; message: string }>(
        `/api/v1/admin/tenants/${tenantId}/reset-data`,
        { method: 'POST', body: JSON.stringify({ confirm: resetConfirm }) },
      )
      setResetResult(result.message)
      setResetConfirm('')
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenant-detail', tenantId] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenant-jobs', tenantId] })
    } catch (e: unknown) {
      setResetError(e instanceof Error ? e.message : 'Reset fallito')
    } finally {
      setResetLoading(false)
    }
  }

  async function handleDelete() {
    const expected = `DELETE ${tenant.name}`
    if (deleteConfirm !== expected) {
      setDeleteError(`Scrivi esattamente: ${expected}`)
      return
    }
    setDeleteLoading(true)
    setDeleteError(null)
    try {
      await adminFetch(
        `/api/v1/admin/tenants/${tenantId}`,
        { method: 'DELETE', body: JSON.stringify({ confirm: deleteConfirm, delete_users: deleteUsers }) },
      )
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] })
      navigate('/admin/tenants', { replace: true })
    } catch (e: unknown) {
      setDeleteError(e instanceof Error ? e.message : 'Eliminazione fallita')
      setDeleteLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Sezione Reset dati */}
      <div className="rounded-xl border p-5 space-y-4" style={sectionStyle}>
        <div>
          <h3 className="text-sm font-semibold mb-1" style={{ color: 'var(--km-text-muted)' }}>
            🗑️ RESET DATI OPERATIVI
          </h3>
          <p className="text-xs leading-relaxed" style={{ color: 'var(--km-text-secondary)' }}>
            Rimuove tutti i dati di business del tenant (ordini, fatture, ore, contratti, ingestion job, sorgenti, shared links, business review).
            Il tenant, le impostazioni e gli utenti associati vengono <strong>preservati</strong>.
            La cache KPI viene svuotata automaticamente.
          </p>
        </div>

        <div
          className="rounded-lg border p-3 text-xs"
          style={{ borderColor: 'rgba(234,179,8,0.3)', background: 'rgba(234,179,8,0.07)', color: '#ca8a04' }}
        >
          ⚠️ Operazione irreversibile. I dati eliminati non possono essere recuperati.
        </div>

        <div>
          <label className="text-xs block mb-1.5" style={{ color: 'var(--km-text-muted)' }}>
            Digita <strong style={{ color: 'var(--km-text-primary)' }}>RESET {tenant.name}</strong> per confermare
          </label>
          <input
            type="text"
            value={resetConfirm}
            onChange={(e) => { setResetConfirm(e.target.value); setResetError(null) }}
            placeholder={`RESET ${tenant.name}`}
            className="w-full px-3 py-2 rounded-lg text-sm border outline-none font-mono"
            style={inputStyle}
          />
        </div>

        {resetError && (
          <p className="text-xs" style={{ color: 'var(--km-error)' }}>{resetError}</p>
        )}
        {resetResult && (
          <p className="text-xs" style={{ color: 'var(--km-success)' }}>✅ {resetResult}</p>
        )}

        <button
          onClick={handleReset}
          disabled={resetLoading || resetConfirm !== `RESET ${tenant.name}`}
          className="px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40"
          style={{ background: '#d97706', color: '#fff' }}
        >
          {resetLoading ? 'Reset in corso…' : 'Azzera dati operativi'}
        </button>
      </div>

      {/* Sezione Elimina tenant */}
      <div
        className="rounded-xl border p-5 space-y-4"
        style={{ borderColor: 'rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.03)' }}
      >
        <div>
          <h3 className="text-sm font-semibold mb-1" style={{ color: '#ef4444' }}>
            ☠️ ELIMINAZIONE TENANT
          </h3>
          <p className="text-xs leading-relaxed" style={{ color: 'var(--km-text-secondary)' }}>
            Elimina il tenant, tutti i suoi dati operativi, le membership e (opzionalmente) gli account Supabase Auth associati.
            Il tenant viene contrassegnato come <code>deleted_at</code> e rimosso dalla lista.
          </p>
        </div>

        <div
          className="rounded-lg border p-3 text-xs"
          style={{ borderColor: 'rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)', color: '#ef4444' }}
        >
          ⛔ Operazione permanente e irreversibile. Il tenant non potrà essere recuperato.
          Gli utenti Supabase eliminati perderanno l'accesso immediatamente.
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer select-none" style={{ color: 'var(--km-text-secondary)' }}>
          <input
            type="checkbox"
            checked={deleteUsers}
            onChange={(e) => setDeleteUsers(e.target.checked)}
            className="rounded"
          />
          Elimina anche gli account Supabase Auth associati ({tenant.users.length} utenti)
        </label>

        <div>
          <label className="text-xs block mb-1.5" style={{ color: 'var(--km-text-muted)' }}>
            Digita <strong style={{ color: 'var(--km-text-primary)' }}>DELETE {tenant.name}</strong> per confermare
          </label>
          <input
            type="text"
            value={deleteConfirm}
            onChange={(e) => { setDeleteConfirm(e.target.value); setDeleteError(null) }}
            placeholder={`DELETE ${tenant.name}`}
            className="w-full px-3 py-2 rounded-lg text-sm border outline-none font-mono"
            style={{ ...inputStyle, borderColor: 'rgba(239,68,68,0.3)' }}
          />
        </div>

        {deleteError && (
          <p className="text-xs" style={{ color: 'var(--km-error)' }}>{deleteError}</p>
        )}

        <button
          onClick={handleDelete}
          disabled={deleteLoading || deleteConfirm !== `DELETE ${tenant.name}`}
          className="px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40"
          style={{ background: '#dc2626', color: '#fff' }}
        >
          {deleteLoading ? 'Eliminazione…' : '⛔ Elimina tenant definitivamente'}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AdminTenantDetail() {
  const { tenantId } = useParams<{ tenantId: string }>()
  const navigate = useNavigate()
  const { adminUser } = useAdminAuth()
  const [activeTab, setActiveTab] = useState<'panoramica' | 'jobs' | 'configurazione' | 'lifecycle'>('panoramica')
  const [impersonating, setImpersonating] = useState(false)

  async function handleImpersonate() {
    if (!tenantId || !data) return
    if (!window.confirm(`Aprire la dashboard di "${data.name}" in modalità supporto?\n${adminUser?.role !== 'superadmin' ? 'Modalità READ-ONLY attiva.' : 'Accesso completo (superadmin).'}`)) return
    setImpersonating(true)
    try {
      const result = await adminFetch<{
        token: string; tenant_id: string; tenant_name: string
        impersonator_email: string; is_readonly: boolean
      }>(`/api/v1/admin/tenants/${tenantId}/impersonate`, { method: 'POST' })

      const params = new URLSearchParams({
        impersonation_token: result.token,
        tenant_id: result.tenant_id,
        tenant_name: result.tenant_name,
        impersonator: result.impersonator_email,
        readonly: String(result.is_readonly),
      })
      window.open(`/?${params.toString()}`, '_blank')
    } catch (e: unknown) {
      alert(`Errore: ${e instanceof Error ? e.message : 'Impersonazione fallita'}`)
    } finally {
      setImpersonating(false)
    }
  }

  const { data, isLoading, error } = useQuery<TenantDetail>({
    queryKey: ['admin', 'tenant-detail', tenantId],
    queryFn: () => adminFetch(`/api/v1/admin/tenants/${tenantId}`),
    enabled: !!tenantId,
  })

  const TAB_STYLE_ACTIVE: React.CSSProperties = {
    color: 'var(--km-text-primary)',
    borderBottomColor: '#6366f1',
  }
  const TAB_STYLE_INACTIVE: React.CSSProperties = {
    color: 'var(--km-text-muted)',
    borderBottomColor: 'transparent',
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/admin/tenants')}
          className="text-sm transition-colors"
          style={{ color: 'var(--km-text-muted)' }}
        >
          ← Tenant
        </button>
        <span style={{ color: 'var(--km-border)' }}>/</span>
        {isLoading ? (
          <span className="text-sm" style={{ color: 'var(--km-text-muted)' }}>Caricamento…</span>
        ) : data ? (
          <div className="flex items-center gap-3 flex-1 flex-wrap">
            <h2 className="text-xl font-semibold" style={{ color: 'var(--km-text-primary)' }}>
              {data.name}
            </h2>
            <Badge value={data.plan} colorMap={PLAN_COLORS} />
            <div className="ml-auto">
              <button
                onClick={handleImpersonate}
                disabled={impersonating}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-40"
                style={{
                  background: 'rgba(180,83,9,0.15)',
                  borderColor: 'rgba(180,83,9,0.4)',
                  color: '#f59e0b',
                }}
              >
                {impersonating ? 'Apertura…' : '👁 Impersona'}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {error && (
        <div
          className="rounded-xl border p-5 text-sm"
          style={{ borderColor: 'var(--km-border)', color: 'var(--km-error)' }}
        >
          Errore nel caricamento del tenant.
        </div>
      )}

      {/* Tabs */}
      <div
        className="flex border-b gap-6"
        style={{ borderColor: 'var(--km-border)' }}
      >
        {([
          { key: 'panoramica', label: 'Panoramica' },
          { key: 'jobs', label: 'Upload & Ingestion' },
          { key: 'configurazione', label: 'Configurazione' },
          { key: 'lifecycle', label: '⚠️ Lifecycle' },
        ] as const).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className="pb-2 text-sm font-medium border-b-2 transition-colors"
            style={activeTab === key ? TAB_STYLE_ACTIVE : TAB_STYLE_INACTIVE}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {!isLoading && data && activeTab === 'panoramica' && <TabPanoramica tenant={data} />}
      {tenantId && activeTab === 'jobs' && <TabJobs tenantId={tenantId} />}
      {!isLoading && data && tenantId && activeTab === 'configurazione' && (
        <TabConfigurazione tenant={data} tenantId={tenantId} />
      )}
      {!isLoading && data && tenantId && activeTab === 'lifecycle' && (
        <TabLifecycle tenant={data} tenantId={tenantId} />
      )}
    </div>
  )
}
